import type { StreakRecord } from '@/domain/streak'
import type { StreakRepository } from '@/repositories/StreakRepository'

const STORAGE_KEY = 'cybertales.streaks'

function toDateKey(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function isConsecutiveDay(prevDateKey: string, nextDateKey: string): boolean {
  const prev = new Date(`${prevDateKey}T00:00:00`)
  const next = new Date(`${nextDateKey}T00:00:00`)
  const diffDays = Math.round((next.getTime() - prev.getTime()) / (1000 * 60 * 60 * 24))
  return diffDays === 1
}

function readAll(): StreakRecord[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? (JSON.parse(raw) as StreakRecord[]) : []
  } catch {
    return []
  }
}

function writeAll(items: StreakRecord[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items))
  } catch {
    // best-effort — streaks simply won't persist across reloads.
  }
}

// Zero-cost implementation of StreakRepository (Document 11 §4, P4).
// Streak math (same day = no-op, consecutive day = +1, gap = reset to 1)
// lives here once, exactly like mergeProgress() centralizes the P1.1
// completion-merge rule — nothing else in the app recomputes it.
export class LocalStreakRepository implements StreakRepository {
  async getForUser(userId: string): Promise<StreakRecord | null> {
    return readAll().find((s) => s.userId === userId) ?? null
  }

  async recordActivity(userId: string, activityDate: Date = new Date()): Promise<StreakRecord> {
    const all = readAll()
    const idx = all.findIndex((s) => s.userId === userId)
    const todayKey = toDateKey(activityDate)
    const existing = idx >= 0 ? all[idx] : null

    let record: StreakRecord
    if (!existing || !existing.lastActivityDate) {
      record = { userId, currentStreak: 1, longestStreak: 1, lastActivityDate: todayKey, updatedAt: new Date().toISOString() }
    } else if (existing.lastActivityDate === todayKey) {
      // Already recorded today — no-op, prevents farming a streak by
      // completing multiple activities in the same day.
      record = existing
    } else if (isConsecutiveDay(existing.lastActivityDate, todayKey)) {
      const currentStreak = existing.currentStreak + 1
      record = {
        userId,
        currentStreak,
        longestStreak: Math.max(existing.longestStreak, currentStreak),
        lastActivityDate: todayKey,
        updatedAt: new Date().toISOString(),
      }
    } else {
      // Gap of 2+ days (or activity recorded "in the past" relative to the
      // stored date) — streak resets to 1, longestStreak is preserved.
      record = {
        userId,
        currentStreak: 1,
        longestStreak: existing.longestStreak,
        lastActivityDate: todayKey,
        updatedAt: new Date().toISOString(),
      }
    }

    if (idx >= 0) all[idx] = record
    else all.push(record)
    writeAll(all)
    return record
  }
}
